import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarClock,
  CircleCheck,
  CirclePlay,
  CircleSlash,
  Clock,
  MapPin,
  Undo2,
  UserCog,
} from 'lucide-react'
import { formatDateTime, OPERATIONS_TIME_ZONE_LABEL } from '@/lib/datetime'
import { formatCurrency, humanizeEnum, pluralize } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminTrip } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { fetchEligibleDrivers } from '@/features/drivers/api'
import { RefundDialog } from '@/features/refunds/RefundDialog'
import { PaymentDetailsPanel } from '@/features/payments/PaymentDetailsPanel'
import { VerifyPaymentDialog, type VerificationDecision } from '@/features/payments/VerifyPaymentDialog'
import { CancelBookingDialog } from '@/features/trip-requests/CancelBookingDialog'
import { EditScheduleDialog, type ScheduleTarget } from '@/features/scheduling/EditScheduleDialog'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { Select } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ChangeHistory } from './ChangeHistory'
import { endTrip, modifyTripDriver, startTrip } from './api'
import styles from './TripDetailsDrawer.module.css'

interface TripDetailsDrawerProps {
  trip: AdminTrip | null
  onClose: () => void
}

const TRIP_INVALIDATIONS = [
  queryKeys.trips.all,
  queryKeys.dashboard.all,
  queryKeys.drivers.all,
  // A reassignment is recorded in the booking's history, which is on screen.
  queryKeys.changeLog.all,
]

/**
 * Everything about one trip, plus every operation an admin can perform on it.
 *
 * The legacy dashboard spread these across three near-identical tables — one to
 * change the driver, one to change the times, one to start and end. They belong
 * on the record they act on, where the current status is visible and the
 * server's own `can*` flags decide what is offered.
 */
export function TripDetailsDrawer({ trip, onClose }: TripDetailsDrawerProps) {
  const open = trip !== null

  const [driverId, setDriverId] = useState('')
  const [confirming, setConfirming] = useState<'start' | 'end' | null>(null)
  const [refunding, setRefunding] = useState<number | null>(null)
  const [cancelling, setCancelling] = useState<number | null>(null)
  const [verifying, setVerifying] = useState<VerificationDecision | null>(null)
  const [rescheduling, setRescheduling] = useState<ScheduleTarget | null>(null)

  useEffect(() => {
    if (!trip) return
    setDriverId(trip.driverId ? String(trip.driverId) : '')
    setConfirming(null)
  }, [trip])

  const driversQuery = useQuery({
    queryKey: queryKeys.drivers.eligible,
    queryFn: fetchEligibleDrivers,
    enabled: open && (trip?.canModifyDriver ?? false),
    staleTime: 30_000,
  })

  const driverMutation = useAdminMutation({
    mutationFn: (variables: { tripId: number; driverId: number }) =>
      modifyTripDriver(variables.tripId, variables.driverId),
    successTitle: 'Driver reassigned',
    invalidate: TRIP_INVALIDATIONS,
    errorTitle: 'Could not reassign the driver',
  })

  const startMutation = useAdminMutation({
    mutationFn: (tripId: number) => startTrip(tripId),
    successTitle: 'Trip started',
    invalidate: TRIP_INVALIDATIONS,
    errorTitle: 'Could not start the trip',
    onSuccess: () => {
      setConfirming(null)
      onClose()
    },
  })

  const endMutation = useAdminMutation({
    mutationFn: (tripId: number) => endTrip(tripId),
    successTitle: 'Trip completed',
    invalidate: TRIP_INVALIDATIONS,
    errorTitle: 'Could not end the trip',
    onSuccess: () => {
      setConfirming(null)
      onClose()
    },
  })

  const busy = driverMutation.isPending || startMutation.isPending || endMutation.isPending

  const driverChanged = trip !== null && driverId !== '' && Number(driverId) !== trip.driverId

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        variant="drawer"
        busy={busy}
        title={trip?.bookingReference ?? `Trip #${trip?.id ?? ''}`}
        description="Trip record and the operations available for its current status."
        footer={
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Close
          </Button>
        }
      >
        {trip ? (
          <div className={styles.body}>
            <section className={styles.statusRow}>
              <StatusBadge status={trip.status} />
              {trip.serviceType ? <span className={styles.chip}>{humanizeEnum(trip.serviceType)}</span> : null}
              {trip.packageName ? <span className={styles.chip}>{trip.packageName}</span> : null}
              <span className={styles.timezoneNote}>All times in {OPERATIONS_TIME_ZONE_LABEL} time</span>
            </section>

            {/* Lifecycle -------------------------------------------------- */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <CalendarClock aria-hidden="true" />
                Lifecycle
              </h3>

              <ol className={styles.timeline} role="list">
                <li className={trip.startedAt ? styles.timelineDone : styles.timelinePending}>
                  <span className={styles.timelineLabel}>Scheduled start</span>
                  <span className={styles.timelineValue}>{formatDateTime(trip.startTime)}</span>
                </li>
                <li className={trip.startedAt ? styles.timelineDone : styles.timelinePending}>
                  <span className={styles.timelineLabel}>Actual start</span>
                  <span className={styles.timelineValue}>
                    {trip.startedAt ? formatDateTime(trip.startedAt) : 'Not started yet'}
                  </span>
                </li>
                <li className={trip.endedAt ? styles.timelineDone : styles.timelinePending}>
                  <span className={styles.timelineLabel}>Scheduled end</span>
                  <span className={styles.timelineValue}>{formatDateTime(trip.endTime)}</span>
                </li>
                <li className={trip.endedAt ? styles.timelineDone : styles.timelinePending}>
                  <span className={styles.timelineLabel}>Actual end</span>
                  <span className={styles.timelineValue}>
                    {trip.endedAt ? formatDateTime(trip.endedAt) : 'Not ended yet'}
                  </span>
                </li>
              </ol>

              {trip.canStart || trip.canEnd || trip.canModifyTimes || trip.canCancel ? (
                <div className={styles.lifecycleActions}>
                  {trip.canStart ? (
                    <Button
                      variant="primary"
                      iconLeft={<CirclePlay aria-hidden="true" />}
                      onClick={() => setConfirming('start')}
                      disabled={busy}
                    >
                      Start trip
                    </Button>
                  ) : null}
                  {trip.canEnd ? (
                    <Button
                      variant="danger"
                      iconLeft={<CircleCheck aria-hidden="true" />}
                      onClick={() => setConfirming('end')}
                      disabled={busy}
                    >
                      End trip
                    </Button>
                  ) : null}
                  {trip.canModifyTimes ? (
                    <Button
                      variant="secondary"
                      iconLeft={<Clock aria-hidden="true" />}
                      onClick={() =>
                        setRescheduling({
                          kind: 'trip',
                          id: trip.id,
                          reference: trip.bookingReference,
                          customerName: trip.userName,
                          startTime: trip.startTime,
                          endTime: trip.endTime,
                        })
                      }
                      disabled={busy}
                    >
                      Change times
                    </Button>
                  ) : null}
                  {/*
                    Cancelling acts on the booking, so it needs the request id.
                    This is the action the trips screen had no button for at
                    all: a confirmed trip with a driver on it could only be
                    called off by ringing the driver and leaving the booking —
                    and its payment — standing.
                  */}
                  {trip.canCancel && trip.requestId ? (
                    <Button
                      variant="danger"
                      iconLeft={<CircleSlash aria-hidden="true" />}
                      onClick={() => setCancelling(trip.requestId)}
                      disabled={busy}
                    >
                      Cancel trip
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p className={styles.note}>
                  {trip.status === 'COMPLETED'
                    ? 'This trip is complete. Its record is read-only.'
                    : trip.status === 'CANCELLED'
                      ? 'This trip was cancelled. Its record is read-only.'
                      : 'No lifecycle action is available for this status.'}
                </p>
              )}
            </section>

            {/* People ----------------------------------------------------- */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <UserCog aria-hidden="true" />
                Passenger &amp; driver
              </h3>

              <KeyValueGrid>
                <KeyValue label="Passenger">
                  {trip.userName ?? '—'}
                  {trip.userPhone ? <div className={styles.muted}>{trip.userPhone}</div> : null}
                </KeyValue>
                <KeyValue label="Assigned driver">
                  {trip.driverName ? (
                    <span className={styles.driverValue}>
                      <Avatar name={trip.driverName} src={trip.driverImageUrl} size="sm" />
                      <span>
                        {trip.driverName}
                        {trip.driverPhone ? <div className={styles.muted}>{trip.driverPhone}</div> : null}
                      </span>
                    </span>
                  ) : (
                    <span className={styles.muted}>Not assigned</span>
                  )}
                </KeyValue>
              </KeyValueGrid>

              {trip.canModifyDriver ? (
                <div className={styles.editRow}>
                  <Select
                    value={driverId}
                    onChange={(event) => setDriverId(event.target.value)}
                    disabled={busy || driversQuery.isLoading}
                    aria-label="Reassign driver"
                  >
                    <option value="">
                      {driversQuery.isLoading ? 'Loading drivers…' : 'Select a driver…'}
                    </option>
                    {(driversQuery.data ?? []).map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name ?? `Driver #${driver.id}`}
                        {driver.activeTripCount > 0
                          ? ` — ${driver.activeTripCount} active ${pluralize(driver.activeTripCount, 'trip')}`
                          : ' — free'}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="secondary"
                    loading={driverMutation.isPending}
                    disabled={!driverChanged || busy}
                    onClick={() => driverMutation.mutate({ tripId: trip.id, driverId: Number(driverId) })}
                  >
                    Reassign
                  </Button>
                </div>
              ) : null}
            </section>

            {/* Journey ---------------------------------------------------- */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <MapPin aria-hidden="true" />
                Journey
              </h3>
              <KeyValueGrid>
                <KeyValue label="Pickup" wide>
                  {trip.pickupAddress ?? '—'}
                </KeyValue>
                <KeyValue label="Drop-off" wide>
                  {trip.dropoffAddress ?? 'Not set'}
                </KeyValue>
                <KeyValue label="Category">{trip.categoryName ?? '—'}</KeyValue>
                <KeyValue label="Car">{trip.carModel ?? '—'}</KeyValue>
                <KeyValue label="Transmission">{humanizeEnum(trip.transmissionMode)}</KeyValue>
                <KeyValue label="Driver arrived">
                  {trip.driverArrivedAt ? formatDateTime(trip.driverArrivedAt) : 'Not recorded'}
                </KeyValue>
              </KeyValueGrid>
            </section>

            {/* Money ------------------------------------------------------ */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Charges</h3>
              <KeyValueGrid>
                <KeyValue label="Base fare">{formatCurrency(trip.basePrice)}</KeyValue>
                <KeyValue label="Extra hours">
                  {trip.extraHoursCount ?? 0} {pluralize(trip.extraHoursCount ?? 0, 'hour')}
                  {typeof trip.extraHoursCost === 'number' && trip.extraHoursCost > 0
                    ? ` · ${formatCurrency(trip.extraHoursCost)}`
                    : ''}
                </KeyValue>
                <KeyValue label="Total">{formatCurrency(trip.totalPrice)}</KeyValue>
                <KeyValue label="Base payment">
                  <StatusBadge status={trip.basePaymentStatus} fallback="Not applicable" />
                </KeyValue>
                <KeyValue label="Extra payment">
                  <StatusBadge status={trip.extraPaymentStatus} fallback="Not applicable" />
                </KeyValue>
                {trip.paymobTransactionId ? (
                  <KeyValue label="Paymob transaction">
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                      {trip.paymobTransactionId}
                    </span>
                  </KeyValue>
                ) : null}
              </KeyValueGrid>

              {/* Offered only when the server says this payment is refundable —
                  a card charge Paymob can reverse, or a verified Instapay
                  transfer, which comes back as points. The dialog re-reads full
                  eligibility and words itself accordingly. */}
              {trip.refundCandidate && trip.requestId ? (
                <div className={styles.lifecycleActions}>
                  <Button
                    variant="secondary"
                    iconLeft={<Undo2 aria-hidden="true" />}
                    onClick={() => setRefunding(trip.requestId)}
                    disabled={busy}
                  >
                    Refund payment
                  </Button>
                </div>
              ) : null}
            </section>

            {/* Payment ---------------------------------------------------- */}
            {/*
              The method, the split between points and external payment, the
              verification state and the proof — all read from the booking
              behind this trip, which is where the payment actually lives. The
              Charges section above is about the trip's own price including
              extra hours; this is about the money that was collected for it.
            */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Payment</h3>
              <PaymentDetailsPanel
                tripRequestId={trip.requestId}
                payment={trip.payment}
                bookingReference={trip.bookingReference}
                onVerify={() => setVerifying('verify')}
                onReject={() => setVerifying('reject')}
                onChanged={onClose}
              />
            </section>

            {/* History ---------------------------------------------------- */}
            <ChangeHistory tripId={trip.id} />

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Record</h3>
              <KeyValueGrid>
                <KeyValue label="Trip ID">{trip.id}</KeyValue>
                <KeyValue label="Request ID">{trip.requestId ?? '—'}</KeyValue>
                <KeyValue label="Created">{formatDateTime(trip.createdAt)}</KeyValue>
              </KeyValueGrid>
            </section>
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={confirming === 'start'}
        title="Start this trip?"
        message={
          <>
            The trip will move to <strong>Started</strong> and the actual start time will be recorded as now, against
            your administrator account. The passenger and driver apps will reflect this immediately.
          </>
        }
        confirmLabel="Start trip"
        loading={startMutation.isPending}
        onConfirm={() => trip && startMutation.mutate(trip.id)}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming === 'end'}
        title="End this trip?"
        message={
          <>
            The trip will be marked <strong>Completed</strong> and the actual end time recorded as now. This closes the
            journey for the passenger and frees the driver. It cannot be undone.
          </>
        }
        confirmLabel="End trip"
        tone="danger"
        loading={endMutation.isPending}
        onConfirm={() => trip && endMutation.mutate(trip.id)}
        onCancel={() => setConfirming(null)}
      />

      <RefundDialog tripRequestId={refunding} onClose={() => setRefunding(null)} />

      <VerifyPaymentDialog
        tripRequestId={verifying && trip ? trip.requestId : null}
        bookingReference={trip?.bookingReference ?? null}
        payment={trip?.payment ?? null}
        decision={verifying ?? 'verify'}
        onClose={() => setVerifying(null)}
      />

      <EditScheduleDialog target={rescheduling} onClose={() => setRescheduling(null)} />

      {/*
        The same dialog the approval queue uses, opened on this trip's booking.
        Cancelling from here goes through the identical policy, refund and
        penalty path — there is no second, weaker cancellation for trips.
      */}
      {/*
        The drawer deliberately stays open behind it. A cancellation invalidates
        the trips list, the page re-reads the open record from the fresh page,
        and the operator watches the trip turn Cancelled with its actions gone —
        rather than being returned to the table wondering whether it worked.
      */}
      <CancelBookingDialog tripRequestId={cancelling} onClose={() => setCancelling(null)} />
    </>
  )
}
